import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { TProductWithRelation } from '@/types/product-type';
import { Package } from 'lucide-react';
import { Control, useWatch } from 'react-hook-form';

interface ProductVariantRowProps {
  variant: TProductWithRelation['productVariants'][0];
  control: Control<Record<string, number | undefined>>;
  isSubmitting: boolean;
  futureStock: number;
  isLast?: boolean;
}

export function ProductVariantRow({
  variant,
  control,
  isSubmitting,
  futureStock,
  isLast = false,
}: ProductVariantRowProps) {
  const closingQty = variant.stock?.closingQty ?? 0;
  const currentStock = closingQty;

  // Watch the input value to show future stock when user enters a value
  const inputValue = useWatch({
    control,
    name: `${variant.id}`,
  });

  const hasInputValue = inputValue !== undefined && inputValue !== null && inputValue !== 0;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(price);
  };

  return (
    <div
      className={`
        transition-all duration-150
        border-b border-slate-200/40
        bg-slate-50/20
        hover:bg-blue-50/40
        group
        flex flex-row flex-nowrap items-center gap-1 sm:gap-2 py-2 sm:py-3 px-2 sm:px-2
      `}
    >
      {/* Variant Column */}
      <div className="flex items-center space-x-1 sm:space-x-2 min-w-0 pl-6 sm:pl-12 relative flex-1 sm:flex-1">
        {/* Tree connector lines */}
        <div className="absolute left-0 top-0 bottom-0 w-6 sm:w-12 flex items-center pointer-events-none">
          {/* Vertical line from top */}
          <div
            className={`absolute left-3 sm:left-6 top-0 w-px bg-slate-300 ${
              isLast ? 'h-1/2' : 'h-full'
            }`}
          />
          {/* Horizontal connector line */}
          <div
            className="absolute left-3 sm:left-6 top-1/2 w-3 sm:w-6 h-px bg-slate-300"
            style={{ transform: 'translateY(-50%)' }}
          />
        </div>
        <Avatar className="h-7 w-7 sm:h-8 sm:w-8 ring-1 ring-slate-100 shrink-0">
          <AvatarImage src={variant.imageUrl || undefined} />
          <AvatarFallback className="bg-gradient-to-br from-blue-50 to-indigo-100 text-slate-600 font-semibold text-xs sm:text-sm">
            {variant.name.substring(0, 2).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="font-medium text-slate-900 text-xs sm:text-sm leading-tight truncate">
            {variant.name}
          </div>
          <div className="flex items-center space-x-1 sm:space-x-1.5 min-w-0 mt-0.5">
            <Package className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-slate-400 shrink-0" />
            <span className="text-xs sm:text-sm font-mono text-slate-500 truncate min-w-0">
              {variant.sku || 'N/A'}
            </span>

            <div className="flex py-2 sm:py-3 px-2 text-right min-w-0 items-center justify-end">
              <span className="text-xs sm:text-sm font-bold text-green-700">
                {variant.price ? formatPrice(variant.price) : 'N/A'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Stock Column */}
      <div className="py-2 sm:py-3 px-1 sm:px-2 text-center shrink-0">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1.5">
          <div className="flex items-center gap-1 sm:gap-1.5">
            <div
              className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                currentStock > 0 ? 'bg-blue-500' : currentStock === 0 ? 'bg-orange-500' : 'bg-red-500'
              }`}
            />
            <span className="text-xs sm:text-sm font-bold text-slate-900 whitespace-nowrap">
              {currentStock.toLocaleString()}
            </span>
          </div>
          {hasInputValue && (
            <div className="flex items-center gap-0.5 sm:gap-1.5">
              <span className="text-xs sm:text-sm text-slate-500 whitespace-nowrap">→</span>
              <span className="text-xs sm:text-sm font-bold text-green-700 whitespace-nowrap">
                {futureStock.toLocaleString()}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Quantity Input Column */}
      <div className="w-16 sm:w-24 shrink-0 py-2 sm:py-3 px-1 sm:px-2">
        <FormField
          control={control}
          name={`${variant.id}`}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  type="number"
                  min="0"
                  placeholder="0"
                  disabled={isSubmitting}
                  value={field.value === 0 || field.value === undefined ? '' : field.value}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      field.onChange(0);
                      return;
                    }
                    const parsed = parseInt(raw, 10);
                    if (!Number.isNaN(parsed)) {
                      field.onChange(parsed);
                    }
                  }}
                  onBlur={(e) => {
                    if (e.target.value === '') {
                      field.onChange(0);
                    }
                  }}
                  className="text-center h-9 sm:h-9 w-full"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
